import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { TimeEntry } from '../types';

export interface TimeLogEntry extends TimeEntry {
  task_title?: string | null;
}

export function useTimeLog() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<TimeLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('intern_time_entries')
        .select('*, intern_project_tasks(title)')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false })
        .limit(100);
      if (err) throw err;
      const mapped: TimeLogEntry[] = (data ?? []).map((e: any) => ({
        ...e,
        task_title: e.intern_project_tasks?.title ?? null,
        intern_project_tasks: undefined,
      }));
      setEntries(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load time log.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const addManualEntry = useCallback(async (params: {
    started_at: string;
    ended_at: string;
    duration_min: number;
    task_id?: string | null;
    note?: string | null;
  }) => {
    if (!user?.id) return { error: new Error('Not logged in') };
    const { data, error } = await supabase
      .from('intern_time_entries')
      .insert({ ...params, user_id: user.id, source: 'manual' })
      .select()
      .single();
    if (!error && data) {
      setEntries((prev) => [data as TimeLogEntry, ...prev]);
    }
    return { error, data };
  }, [user?.id]);

  const editEntry = useCallback(async (
    id: string,
    updates: Partial<Pick<TimeEntry, 'started_at' | 'ended_at' | 'duration_min' | 'task_id' | 'note'>>
  ) => {
    const { data, error } = await supabase
      .from('intern_time_entries')
      .update({ ...updates, edited: true })
      .eq('id', id)
      .select()
      .single();
    if (!error && data) {
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...data } : e)));
    }
    return { error };
  }, []);

  // Compute daily / weekly totals + per-day grouping
  const totals = computeTotals(entries);
  const days = groupByDay(entries);

  return { entries, days, loading, error, totals, addManualEntry, editEntry, refresh: fetchEntries };
}

export interface TimeDay {
  date: string;      // YYYY-MM-DD (America/Denver)
  totalMin: number;
  entries: TimeLogEntry[];
}

// Group entries into worked days, newest day first. Entries arrive newest-first
// so each day's entries keep that order.
function groupByDay(entries: TimeLogEntry[]): TimeDay[] {
  const map = new Map<string, TimeLogEntry[]>();
  for (const e of entries) {
    const date = new Date(e.started_at).toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
    const list = map.get(date);
    if (list) list.push(e);
    else map.set(date, [e]);
  }
  return Array.from(map.entries())
    .map(([date, es]) => ({
      date,
      totalMin: es.reduce((sum, e) => sum + (e.duration_min ?? 0), 0),
      entries: es,
    }))
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

function computeTotals(entries: TimeLogEntry[]) {
  const now = new Date();
  const today = now.toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
  const weekStart = getWeekStart(now);

  let todayMin = 0;
  let weekMin = 0;

  for (const e of entries) {
    const min = e.duration_min ?? 0;
    const dateStr = new Date(e.started_at).toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
    if (dateStr === today) todayMin += min;
    if (new Date(e.started_at) >= weekStart) weekMin += min;
  }

  return { todayMin, weekMin };
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
